import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import styles from './ScrimTracks.module.css';
import { getScrimTracks } from '../utils/api';

interface Track {
  youtube_id: string;
  track_index: number;
  yt_title: string;
  yt_channel: string;
  yt_stream_start_utc: string | null;
  processing_status?: string;
  processing_error?: string;
  rounds?: any[];
}

const ScrimTracks: React.FC = () => {
  const { scrim_id } = useParams<{ scrim_id: string }>();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState<Track[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadScrimTracks = async (showLoading = true) => {
    if (!scrim_id) return;
    
    try {
      if (showLoading) setIsLoading(true);
      setError('');
      const response = await getScrimTracks(scrim_id);
      setTracks(response.tracks || []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tracks');
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (scrim_id) {
      loadScrimTracks();
    }
  }, [scrim_id]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!scrim_id) return;

    const interval = setInterval(() => {
      loadScrimTracks(false); // Don't show loading spinner for auto-refresh
    }, 10000);

    return () => clearInterval(interval);
  }, [scrim_id]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const formatTime = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleTimeString();
    } catch {
      return 'Invalid time';
    }
  };

  const getProcessingStatusDisplay = (status?: string, error?: string) => {
    if (!status) {
      return { 
        text: 'QUEUING', 
        showSpinner: true, 
        spinnerVariant: 'secondary' as const,
        animationType: 'border' as const,
        className: styles.statusText 
      };
    }
    
    switch (status) {
      case 'QUEUED':
        return { 
          text: 'Queued', 
          showSpinner: false,
          className: styles.statusText 
        };
      case 'LAUNCHED':
        return { 
          text: 'Launching', 
          showSpinner: true, 
          spinnerVariant: 'primary' as const,
          animationType: 'border' as const,
          className: styles.statusText 
        };
      case 'INITIALIZING':
        return { 
          text: 'Initializing', 
          showSpinner: true, 
          spinnerVariant: 'info' as const,
          animationType: 'border' as const,
          className: styles.statusText 
        };
      case 'CONNECTING':
        return { 
          text: 'Connecting', 
          showSpinner: true, 
          spinnerVariant: 'warning' as const,
          animationType: 'border' as const,
          className: styles.statusText 
        };
      case 'PROCESSING':
        return { 
          text: 'Processing', 
          showSpinner: true, 
          spinnerVariant: 'info' as const,
          animationType: 'grow' as const,
          className: styles.statusText 
        };
      case 'COMPLETED':
        return { 
          text: 'Completed', 
          showSpinner: false, 
          className: styles.statusTextCompleted 
        };
      case 'ERROR':
        return { 
          text: error ? `Error: ${error}` : 'Error', 
          showSpinner: false,
          className: styles.statusTextError,
          title: error 
        };
      default:
        return { 
          text: status, 
          showSpinner: false, 
          className: styles.statusText 
        };
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          Loading scrim tracks...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <i className="bi bi-exclamation-triangle"></i>
          <div>
            <p>{error}</p>
            <button onClick={() => loadScrimTracks()} className={styles.retryButton}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button 
          className={styles.backButton}
          onClick={() => navigate('/scrims')}
        >
          <i className="bi bi-arrow-left"></i>
          Back to Scrims
        </button>
        
        <div className={styles.headerInfo}>
          <h1>Scrim Tracks</h1>
          {lastUpdated && (
            <p className={styles.lastUpdated}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>

      {tracks.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <i className="bi bi-camera-video"></i>
          </div>
          <h3>No tracks found</h3>
          <p>This scrim doesn't have any tracks yet.</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.tracksTable}>
            <thead>
              <tr>
                <th className={styles.columnIndex}>#</th>
                <th className={styles.columnVideo}>Video</th>
                <th className={styles.columnChannel}>Channel</th>
                <th className={styles.columnDate}>Date</th>
                <th className={styles.columnStatus}>Status</th>
                <th className={styles.columnRounds}>Rounds</th>
              </tr>
            </thead>
            <tbody>
              {tracks
                .sort((a, b) => a.track_index - b.track_index)
                .map((track) => (
                <tr key={track.youtube_id} className={styles.tableRow}>
                  <td className={styles.columnIndex}>
                    <span className={styles.trackNumber}>{track.track_index}</span>
                  </td>
                  
                  <td className={styles.columnVideo}>
                    <div className={styles.videoInfo}>
                      <span className={styles.videoTitle}>
                        {track.yt_title || 'Unknown title'}
                      </span>
                      <span className={styles.videoId}>
                        {track.youtube_id}
                      </span>
                    </div>
                  </td>
                  
                  <td className={styles.columnChannel}>
                    <span className={styles.channelName}>
                      {track.yt_channel || 'Unknown channel'}
                    </span>
                  </td>
                  
                  <td className={styles.columnDate}>
                    <div className={styles.dateInfo}>
                      <span className={styles.date}>{formatDate(track.yt_stream_start_utc)}</span>
                      <span className={styles.time}>{formatTime(track.yt_stream_start_utc)}</span>
                    </div>
                  </td>
                  
                  <td className={styles.columnStatus}>
                    {(() => {
                      const statusDisplay = getProcessingStatusDisplay(track.processing_status, track.processing_error);
                      return (
                        <div className={styles.statusContainer}>
                          {statusDisplay.showSpinner && (
                            <Spinner
                              as="span"
                              animation={statusDisplay.animationType}
                              size="sm"
                              variant={statusDisplay.spinnerVariant}
                              className={styles.statusSpinner}
                            />
                          )}
                          <span 
                            className={statusDisplay.className}
                            title={statusDisplay.title}
                          >
                            {statusDisplay.text}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  
                  <td className={styles.columnRounds}>
                    <span className={styles.roundsCount}>
                      {track.rounds ? track.rounds.length : 0} rounds
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ScrimTracks; 